import { Link } from "react-router";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Badge } from "@/components/ui/badge";
import { 
  Briefcase, 
  Building2, 
  GraduationCap, 
  Sparkles, 
  Target, 
  Users, 
  Zap,
  MapPin,
  Clock,
  Radio
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLatestJobsPreview } from "@/features/landing";
import { useState } from "react";
import { usePrefetchRoute } from "@/hooks/usePrefetchRoute";

interface MegaMenuProps {
  postJobLink: string;
}

// Map nav hrefs to their lazy importers so we can prefetch on hover/focus.
// Keep keys exactly as the `href` strings used below.
const ROUTE_IMPORTERS: Record<string, () => Promise<unknown>> = {
  "/jobs": () => import("@/pages/Jobs"),
  "/premium": () => import("@/pages/Premium"),
  "/learning": () => import("@/pages/Learning"),
  "/learning/courses": () => import("@/pages/LearningCourses"),
  "/services": () => import("@/pages/Services"),
  "/companies": () => import("@/pages/Companies"),
  "/how-it-works": () => import("@/pages/HowItWorks"),
  "/employer": () => import("@/pages/EmployerDashboard"),
  "/employer/dashboard": () => import("@/pages/EmployerDashboard"),
  "/employer/candidates": () => import("@/pages/CandidateSearch"),
  "/employers": () => import("@/pages/Employers"),
  "/post-job": () => import("@/pages/PostJob"),
  "/signal": () => import("@/pages/Signal"),
};

const ListItem = ({
  className,
  title,
  children,
  href,
  icon: Icon,
  badge,
}: {
  className?: string;
  title: string;
  children: React.ReactNode;
  href: string;
  icon?: React.FC<{ className?: string }>;
  badge?: string;
}) => {
  const prefetch = usePrefetchRoute();
  const importer = ROUTE_IMPORTERS[href];
  const handlers = importer ? prefetch(importer) : undefined;
  return (
    <li>
      <NavigationMenuLink asChild>
        <Link
          to={href}
          {...(handlers ?? {})}
          className={cn(
            "group relative block select-none overflow-hidden rounded-xl p-3 leading-none no-underline outline-none",
            "transition-[background,transform,box-shadow] duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]",
            "hover:bg-foreground/[0.035] hover:shadow-[0_1px_0_0_hsl(var(--border)/0.5)_inset]",
            "focus-visible:bg-foreground/[0.05] focus-visible:ring-1 focus-visible:ring-primary/30",
            className
          )}
        >
          {/* Sliding thermal accent bar (left edge) */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-0 top-1/2 h-8 w-[2px] -translate-y-1/2 rounded-r-full bg-gradient-thermal opacity-0 transition-all duration-300 ease-out group-hover:opacity-100 group-focus-visible:opacity-100"
          />
          {/* Soft radial glow on hover */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -left-6 top-1/2 h-24 w-24 -translate-y-1/2 rounded-full bg-gradient-thermal opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-[0.10]"
          />
          <div className="relative flex items-start gap-3">
            {Icon && (
              <div className="relative flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-foreground/[0.04] text-foreground/70 ring-1 ring-border/40 transition-all duration-300 group-hover:text-foreground group-hover:ring-border/70">
                <span
                  aria-hidden="true"
                  className="absolute inset-0 bg-gradient-thermal opacity-0 transition-opacity duration-300 group-hover:opacity-[0.14]"
                />
                <Icon className="relative h-4 w-4" />
              </div>
            )}
            <div className="flex-1 min-w-0 space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium leading-none text-foreground transition-colors">
                  {title}
                </span>
                {badge && (
                  <Badge
                    variant="secondary"
                    className="text-[10px] px-1.5 py-0 bg-foreground/5 text-foreground/70 border border-border/50"
                  >
                    {badge}
                  </Badge>
                )}
              </div>
              <p className="line-clamp-2 text-xs leading-snug text-muted-foreground">
                {children}
              </p>
            </div>
          </div>
        </Link>
      </NavigationMenuLink>
    </li>
  );
};

const FeaturedJobCard = ({
  title,
  company,
  location,
}: {
  title: string;
  company: string;
  location: string;
}) => (
  <div className="group relative overflow-hidden rounded-lg border border-border/50 bg-card/40 p-3 transition-all duration-300 ease-out hover:bg-foreground/[0.035] hover:border-border hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-12px_hsl(var(--primary)/0.25)]">
    <p className="text-sm font-medium line-clamp-1 group-hover:text-primary transition-colors">
      {title}
    </p>
    <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{company}</p>
    <div className="flex items-center gap-1 mt-1.5 text-xs text-muted-foreground">
      <MapPin className="h-3 w-3 flex-shrink-0" />
      <span className="line-clamp-1">{location}</span>
    </div>
  </div>
);

export function MegaMenu({ postJobLink }: MegaMenuProps) {
  // Defer the latest-jobs fetch until the user opens the "Latest Jobs" menu.
  // Reuses the same fast RPC (`get_latest_jobs_preview`) the home page hero
  // uses, so it dedupes via the TanStack Query cache when both are visible.
  const [latestJobsOpen, setLatestJobsOpen] = useState(false);
  const { data: jobs, isLoading } = useLatestJobsPreview({ enabled: latestJobsOpen });
  const prefetch = usePrefetchRoute();
  const jobsPrefetch = prefetch(ROUTE_IMPORTERS["/jobs"]);
  const signalPrefetch = prefetch(ROUTE_IMPORTERS["/signal"]);
  const servicesPrefetch = prefetch(ROUTE_IMPORTERS["/services"]);
  const jobDetailPrefetch = prefetch(() => import("@/pages/JobDetail"));

  return (
    <NavigationMenu hideViewport className="hidden md:flex min-w-0 flex-1 max-w-full justify-start">
      <NavigationMenuList className="flex-wrap justify-start gap-x-1 gap-y-1 space-x-0">
        {/* For Candidates */}
        <NavigationMenuItem className="relative">
          <NavigationMenuTrigger className="group/trigger relative h-9 px-3 bg-transparent hover:bg-foreground/[0.04] data-[state=open]:bg-foreground/[0.06] mono-eyebrow !text-[11px] !tracking-[0.16em] !font-mono text-foreground/70 hover:text-foreground data-[state=open]:text-foreground transition-[background,color] duration-300 ease-out after:absolute after:left-3 after:right-3 after:bottom-1 after:h-[1.5px] after:rounded-full after:bg-gradient-thermal after:opacity-0 after:scale-x-0 after:origin-center after:transition-all after:duration-300 after:ease-out hover:after:opacity-100 hover:after:scale-x-100 data-[state=open]:after:opacity-100 data-[state=open]:after:scale-x-100">
            Candidates
          </NavigationMenuTrigger>
          <NavigationMenuContent className="rounded-2xl border border-border/60 bg-popover/98 text-foreground shadow-xl shadow-black/10 backdrop-blur-xl">
            <div className="w-[400px] md:w-[520px] lg:w-[620px] p-4">
              <p className="mono-eyebrow mb-3 px-3">Candidates</p>
              <ul className="grid gap-1 md:grid-cols-2">
                <ListItem href="/jobs" title="Browse roles" icon={Briefcase}>
                  Search data center jobs by role, location, shift, and clearance level.
                </ListItem>
                <ListItem
                  href="/premium"
                  title="AI tools"
                  icon={Sparkles}
                  badge="Free"
                >
                  Resume tailoring, cover letters, interview prep, and skills-gap analysis — completely free.
                </ListItem>
                <ListItem href="/learning" title="Learning hub" icon={GraduationCap}>
                  Certifications, training resources, and career development paths.
                </ListItem>
                <ListItem href="/learning/courses" title="Course catalog" icon={GraduationCap}>
                  Browse 6,000+ courses from every major provider, filter by skill, level, and price.
                </ListItem>
                <ListItem href="/services" title="Career services" icon={Sparkles}>
                  White-glove resume review, job search coaching, and interview prep.
                </ListItem>
                <ListItem href="/companies" title="Company profiles" icon={Building2}>
                  Explore data center operators, contractors, and hyperscalers.
                </ListItem>
                <ListItem href="/talent" title="Talent Wall" icon={Users} badge="New">
                  Publish a Candidate Card and get discovered by verified employers.
                </ListItem>
                <ListItem href="/how-it-works" title="How it works" icon={Zap}>
                  Learn how Rack & Role connects you with mission-critical roles.
                </ListItem>
              </ul>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>

        {/* For Employers */}
        <NavigationMenuItem className="relative">
          <NavigationMenuTrigger className="group/trigger relative h-9 px-3 bg-transparent hover:bg-foreground/[0.04] data-[state=open]:bg-foreground/[0.06] mono-eyebrow !text-[11px] !tracking-[0.16em] !font-mono text-foreground/70 hover:text-foreground data-[state=open]:text-foreground transition-[background,color] duration-300 ease-out after:absolute after:left-3 after:right-3 after:bottom-1 after:h-[1.5px] after:rounded-full after:bg-gradient-thermal after:opacity-0 after:scale-x-0 after:origin-center after:transition-all after:duration-300 after:ease-out hover:after:opacity-100 hover:after:scale-x-100 data-[state=open]:after:opacity-100 data-[state=open]:after:scale-x-100">
            Employers
          </NavigationMenuTrigger>
          <NavigationMenuContent className="rounded-2xl border border-border/60 bg-popover/98 text-foreground shadow-xl shadow-black/10 backdrop-blur-xl">
            <div className="w-[400px] md:w-[520px] lg:w-[620px] p-4">
              <p className="mono-eyebrow mb-3 px-3">Employers</p>
              <ul className="grid gap-1 md:grid-cols-2">
                <ListItem href={postJobLink} title="Post a role" icon={Target}>
                  Create structured job postings with shift, clearance, and systems requirements.
                </ListItem>
                <ListItem href="/employer" title="Employer dashboard" icon={Building2}>
                  Manage postings, review applicants, and track hiring metrics.
                </ListItem>
                <ListItem href="/employer/candidates" title="Talent search" icon={Users}>
                  Search verified candidates by skills, certs, and availability.
                </ListItem>
                <ListItem href="/talent" title="Talent Wall" icon={Users} badge="New">
                  Browse verified Candidate Cards from job seekers open to work.
                </ListItem>
                <ListItem href="/employers" title="Partner solutions" icon={Sparkles}>
                  White-glove sourcing, concierge support, and verified employer access.
                </ListItem>
              </ul>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>

        {/* Latest Jobs */}
        <NavigationMenuItem
          className="relative"
          onPointerEnter={() => setLatestJobsOpen(true)}
          onFocus={() => setLatestJobsOpen(true)}
        >
          <NavigationMenuTrigger className="group/trigger relative h-9 px-3 bg-transparent hover:bg-foreground/[0.04] data-[state=open]:bg-foreground/[0.06] mono-eyebrow !text-[11px] !tracking-[0.16em] !font-mono text-foreground/70 hover:text-foreground data-[state=open]:text-foreground transition-[background,color] duration-300 ease-out after:absolute after:left-3 after:right-3 after:bottom-1 after:h-[1.5px] after:rounded-full after:bg-gradient-thermal after:opacity-0 after:scale-x-0 after:origin-center after:transition-all after:duration-300 after:ease-out hover:after:opacity-100 hover:after:scale-x-100 data-[state=open]:after:opacity-100 data-[state=open]:after:scale-x-100">
            <span className="flex items-center gap-1.5">
              Latest jobs
              <Badge
                variant="secondary"
                className="text-[9px] px-1.5 py-0 bg-primary/10 text-primary border-0 font-mono uppercase tracking-wider"
              >
                New
              </Badge>
            </span>
          </NavigationMenuTrigger>
          <NavigationMenuContent className="rounded-2xl border border-border/60 bg-popover/98 text-foreground shadow-xl shadow-black/10 backdrop-blur-xl">
            <div className="w-[400px] md:w-[460px] p-4">
              <div className="flex items-center justify-between mb-3 px-1">
                <p className="mono-eyebrow flex items-center gap-1.5">
                  <Clock className="h-3 w-3" />
                  Recently posted
                </p>
                <Link
                  to="/jobs"
                  {...jobsPrefetch}
                  className="text-xs text-primary hover:underline"
                >
                  View all
                </Link>
              </div>
              <div className="space-y-2">
                {isLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-[72px] rounded-lg bg-muted/40 animate-pulse" />
                    ))}
                  </div>
                ) : jobs && jobs.length > 0 ? (
                  jobs.slice(0, 3).map((job) => (
                    <Link key={job.id} to={`/jobs/${job.id}`} {...jobDetailPrefetch}>
                      <FeaturedJobCard
                        title={job.title}
                        company={job.company_name}
                        location={job.location}
                      />
                    </Link>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    No jobs available. Check back soon.
                  </p>
                )}
              </div>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>

        {/* Direct Links */}
        <NavigationMenuItem>
          <NavigationMenuLink asChild>
            <Link
              to="/signal"
              {...signalPrefetch}
              className="group/trigger relative inline-flex h-9 w-max items-center justify-center gap-1.5 rounded-md bg-transparent px-3 py-2 mono-eyebrow !text-[11px] !tracking-[0.16em] !font-mono text-foreground/70 transition-[background,color] duration-300 ease-out hover:bg-foreground/[0.04] hover:text-foreground focus-visible:bg-foreground/[0.05] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/30 after:absolute after:left-3 after:right-3 after:bottom-1 after:h-[1.5px] after:rounded-full after:bg-gradient-thermal after:opacity-0 after:scale-x-0 after:origin-center after:transition-all after:duration-300 after:ease-out hover:after:opacity-100 hover:after:scale-x-100"
            >
              <Radio className="h-3.5 w-3.5" />
              Signal
            </Link>
          </NavigationMenuLink>
        </NavigationMenuItem>

        <NavigationMenuItem>
          <NavigationMenuLink asChild>
            <Link
              to="/services"
              {...servicesPrefetch}
              className="group/trigger relative inline-flex h-9 w-max items-center justify-center rounded-md bg-transparent px-3 py-2 mono-eyebrow !text-[11px] !tracking-[0.16em] !font-mono text-foreground/70 transition-[background,color] duration-300 ease-out hover:bg-foreground/[0.04] hover:text-foreground focus-visible:bg-foreground/[0.05] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/30 after:absolute after:left-3 after:right-3 after:bottom-1 after:h-[1.5px] after:rounded-full after:bg-gradient-thermal after:opacity-0 after:scale-x-0 after:origin-center after:transition-all after:duration-300 after:ease-out hover:after:opacity-100 hover:after:scale-x-100"
            >
              Services
            </Link>
          </NavigationMenuLink>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
}

